import { useEffect, useState, type FormEvent } from "react";
import { Eye, EyeOff, Gift, LogIn, LogOut, ShieldCheck, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button, EmptyState, Notice, Skeleton } from "../components/ui";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { useMember } from "../context/MemberContext";
import { ApiError, apiRequest } from "../lib/api";
import { formatDateTime, formatRupiah } from "../lib/format";
import { normalizeCollection, type ApiRecord } from "../lib/types";
import {
  MEMBER_POINT_VALUE_RUPIAH,
  MEMBER_SPEND_PER_POINT_RUPIAH,
  memberPointDiscount,
} from "../../shared/loyalty";

type MemberMode = "login" | "register" | "change";

interface RegistrationSecurityConfig {
  enabled: boolean;
  siteKey: string;
  actions: {
    memberRegister: string;
    memberLogin: string;
    memberChangePin: string;
  };
}

const displayText = (value: unknown, fallback: string) =>
  typeof value === "string" || typeof value === "number"
    ? String(value)
    : fallback;

export function MemberPage() {
  const { member, loading, login, register, logout, changePin } = useMember();
  const [mode, setMode] = useState<MemberMode>("login");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const [registrationConflict, setRegistrationConflict] = useState<string>();
  const [pointHistory, setPointHistory] = useState<ApiRecord[]>([]);
  const [historyLoadedFor, setHistoryLoadedFor] = useState("");
  const [registrationSecurity, setRegistrationSecurity] = useState<RegistrationSecurityConfig>();
  const [securityLoading, setSecurityLoading] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const memberPhone = member?.whatsappNumber ?? "";
  const securityAction = mode === "register"
    ? registrationSecurity?.actions.memberRegister
    : mode === "change"
      ? registrationSecurity?.actions.memberChangePin
      : registrationSecurity?.actions.memberLogin;

  useEffect(() => {
    if (!memberPhone) return undefined;
    let cancelled = false;
    void apiRequest<unknown>("/api/member/points")
      .then((payload) => {
        if (!cancelled) {
          setPointHistory(normalizeCollection(payload).items);
          setHistoryLoadedFor(memberPhone);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPointHistory([]);
          setHistoryLoadedFor(memberPhone);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [memberPhone]);

  const historyLoading = Boolean(memberPhone && historyLoadedFor !== memberPhone);

  useEffect(() => {
    let cancelled = false;
    void apiRequest<RegistrationSecurityConfig>("/api/member/registration-config")
      .then((config) => {
        if (!cancelled) setRegistrationSecurity(config);
      })
      .catch((caught) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "Pemeriksaan keamanan belum dapat dimuat.");
        }
      })
      .finally(() => {
        if (!cancelled) setSecurityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chooseMode = (nextMode: MemberMode) => {
    setMode(nextMode);
    setError(undefined);
    setRegistrationConflict(undefined);
    setTurnstileToken("");
    setTurnstileResetKey((value) => value + 1);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!turnstileToken) {
      setError("Tunggu pemeriksaan keamanan selesai sebelum melanjutkan.");
      return;
    }
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      if (mode === "change") {
        setNotice(await changePin(phone, pin, newPin, turnstileToken));
        setPin("");
        setNewPin("");
        setMode("login");
      } else if (mode === "register") {
        await register(name, phone, pin, turnstileToken);
      } else {
        await login(phone, pin, turnstileToken);
      }
    } catch (caught) {
      if (
        mode === "register" &&
        caught instanceof ApiError &&
        caught.code === "MEMBER_EXISTS"
      ) {
        setRegistrationConflict(
          "Nomor WhatsApp ini sudah pernah terdaftar dan tidak dapat didaftarkan ulang. Silakan pilih Masuk. Jika lupa PIN, datang atau hubungi Admin toko untuk reset PIN.",
        );
        setError(undefined);
      } else {
        setError(caught instanceof Error ? caught.message : "Permintaan belum berhasil.");
      }
      setTurnstileToken("");
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="page-shell inner-page"><Skeleton rows={5} /></div>;

  if (member && mode !== "change") {
    return (
      <div className="page-shell inner-page member-page">
        <section className="member-balance-card">
          <Gift aria-hidden="true" />
          <span>Saldo poin Anda</span>
          <strong>{member.pointsBalance} poin</strong>
          <small>
            Nilai potongan {formatRupiah(memberPointDiscount(member.pointsBalance))}
          </small>
        </section>
        {member.mustChangePassword && (
          <Notice tone="warning">
            Anda memakai PIN sementara dari Admin. Tukar PIN sebelum berbelanja.
          </Notice>
        )}
        <section className="form-card member-account-card">
          <span className="eyebrow">Akun Member</span>
          <h1>{member.name || "Nama belum diisi"}</h1>
          <p className="member-phone">{member.whatsappNumber}</p>
          <p>
            Setiap {formatRupiah(MEMBER_SPEND_PER_POINT_RUPIAH)} nilai produk pada
            pesanan yang selesai mendapat 1 poin. Satu poin dapat digunakan
            sebagai potongan {formatRupiah(MEMBER_POINT_VALUE_RUPIAH)}.
          </p>
          <div className="member-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setPhone(member.whatsappNumber);
                setMode("change");
              }}
              icon={<ShieldCheck aria-hidden="true" />}
            >
              Tukar PIN
            </Button>
            <Button
              variant="quiet"
              onClick={() => void logout()}
              icon={<LogOut aria-hidden="true" />}
            >
              Keluar
            </Button>
          </div>
          <Link className="button button--primary" to="/">
            Mulai Belanja
          </Link>
        </section>
        <section className="form-card member-point-history">
          <span className="eyebrow">Transparansi poin</span>
          <h2>Riwayat & Alasan Poin</h2>
          <p>
            Bagian ini hanya menjelaskan asal penambahan atau pengurangan poin,
            tanpa menampilkan daftar barang yang pernah dibeli.
          </p>
          {historyLoading && <Skeleton rows={4} />}
          {!historyLoading && pointHistory.length === 0 && (
            <EmptyState title="Belum ada riwayat poin" />
          )}
          {!historyLoading && pointHistory.length > 0 && (
            <div className="point-ledger-list">
              {pointHistory.map((item) => {
                const change = Number(
                  item.pointsChange ?? item.points_change ?? 0,
                );
                const purchase = Number(
                  item.purchaseAmount ?? item.purchase_amount ?? 0,
                );
                return (
                  <div key={displayText(item.id, `point-${change}-${purchase}`)}>
                    <span>
                      <strong>{displayText(item.reason, "Perubahan poin")}</strong>
                      <small>
                        {displayText(
                          item.branchName ?? item.branch_name,
                          "Online / Owner",
                        )}
                        {" · "}
                        {formatDateTime(
                          displayText(item.createdAt ?? item.created_at, ""),
                        )}
                      </small>
                      {purchase > 0 && (
                        <small>Nilai belanja {formatRupiah(purchase)}</small>
                      )}
                    </span>
                    <b className={change >= 0 ? "positive" : "negative"}>
                      {change > 0 ? "+" : ""}
                      {change} poin
                    </b>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="page-shell inner-page member-page">
      <div className="page-heading">
        <span className="eyebrow">Member Warung Sedap Sayur</span>
        <h1>Masuk, Daftar, atau Tukar PIN</h1>
        <p>Daftar dengan nama, nomor WhatsApp, dan PIN 5 angka.</p>
      </div>
      <div className="member-tabs" role="tablist" aria-label="Menu member">
        <button type="button" className={mode === "login" ? "active" : ""} onClick={() => chooseMode("login")}>
          <LogIn aria-hidden="true" /> Masuk
        </button>
        <button type="button" className={mode === "register" ? "active" : ""} onClick={() => chooseMode("register")}>
          <UserPlus aria-hidden="true" /> Daftar
        </button>
        <button type="button" className={mode === "change" ? "active" : ""} onClick={() => chooseMode("change")}>
          <ShieldCheck aria-hidden="true" /> Tukar PIN
        </button>
      </div>
      {mode === "register" && registrationConflict && (
        <Notice tone="danger">{registrationConflict}</Notice>
      )}
      <form className="form-card member-form" onSubmit={(event) => void submit(event)}>
        {notice && <Notice tone="success">{notice}</Notice>}
        {error && <Notice tone="danger">{error}</Notice>}
        {mode === "register" && (
          <label>
            Nama lengkap
            <input
              autoComplete="name"
              minLength={2}
              maxLength={100}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Contoh: Ibu Ayu"
              required
            />
          </label>
        )}
        <label>
          Nomor WhatsApp
          <input
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              setRegistrationConflict(undefined);
            }}
            placeholder="+628..., 08..., atau kode negara lain"
            required
          />
        </label>
        <label>
          {mode === "change" ? "PIN lama" : "PIN 5 angka"}
          <span className="password-input">
            <input
              type={showPin ? "text" : "password"}
              inputMode="numeric"
              pattern="[0-9]{5}"
              minLength={5}
              maxLength={5}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 5))}
              required
            />
            <button type="button" onClick={() => setShowPin((value) => !value)} aria-label={showPin ? "Sembunyikan PIN" : "Tampilkan PIN"}>
              {showPin ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </span>
        </label>
        {mode === "change" && (
          <label>
            PIN baru
            <input
              type={showPin ? "text" : "password"}
              inputMode="numeric"
              pattern="[0-9]{5}"
              minLength={5}
              maxLength={5}
              autoComplete="new-password"
              value={newPin}
              onChange={(event) => setNewPin(event.target.value.replace(/\D/g, "").slice(0, 5))}
              required
            />
          </label>
        )}
        {mode === "register" && (
          <small>
            Nomor yang sudah pernah terdaftar tidak dapat didaftarkan lagi.
            Hubungi Admin toko jika lupa PIN.
          </small>
        )}
        <div className="member-security-check">
          <span><ShieldCheck aria-hidden="true" /> Anti-spam aman dari Cloudflare</span>
          {securityLoading && <small>Memuat pemeriksaan keamanan…</small>}
          {registrationSecurity?.enabled && registrationSecurity.siteKey && securityAction && (
            <TurnstileWidget
              siteKey={registrationSecurity.siteKey}
              action={securityAction}
              resetKey={turnstileResetKey}
              label={`Pemeriksaan keamanan ${mode === "register" ? "pendaftaran" : mode === "change" ? "tukar PIN" : "login member"}`}
              onToken={(token) => {
                setTurnstileToken(token);
                if (token) setError(undefined);
              }}
              onError={setError}
            />
          )}
          {registrationSecurity?.enabled && (
            <small>
              Jika pemeriksaan tidak muncul, izinkan <strong>challenges.cloudflare.com</strong> pada
              pemblokir iklan lalu muat ulang halaman.
            </small>
          )}
          {registrationSecurity && !registrationSecurity.enabled && (
            <Notice tone="warning">Pemeriksaan keamanan sedang dipersiapkan. Hubungi Admin toko bila perlu bantuan.</Notice>
          )}
        </div>
        {mode === "change" && (
          <small>Lupa PIN lama? Hubungi Admin toko untuk mendapat PIN sementara baru.</small>
        )}
        <Button
          type="submit"
          busy={busy}
          className="button--full"
          disabled={!registrationSecurity?.enabled || !turnstileToken}
        >
          {mode === "login" ? "Masuk Member" : mode === "register" ? "Daftar Member" : "Tukar PIN"}
        </Button>
      </form>
    </div>
  );
}
